/**
 * src/db/scenarios.ts — scenario CRUD over IndexedDB (§9).
 *
 * Every record is migrated and Zod-validated on read (§8.2, §9.2). Upgraded records are
 * written back once; corrupt/legacy records are moved to the quarantine store (never
 * deleted); records from a newer app version load read-only.
 */
import { CURRENT_SCHEMA_VERSION } from '@/schema';
import type { Scenario, ScenarioInputs } from '@/types';
import { getDb } from './index';
import { migrateRecord } from './migrations';

export interface LoadedScenarios {
  scenarios: Scenario[];
  /** IDs that came from a newer app version and must render read-only (§9.2). */
  readOnlyIds: string[];
  /** Count of records moved to quarantine on this read. */
  quarantined: number;
}

function asRecord(scenario: Scenario): Record<string, unknown> {
  return scenario as unknown as Record<string, unknown>;
}

/** Load all scenarios, migrating/validating each; sorted by most-recently-updated. */
export async function listScenarios(): Promise<LoadedScenarios> {
  const db = await getDb();
  const rawAll = await db.getAll('scenarios');
  const scenarios: Scenario[] = [];
  const readOnlyIds: string[] = [];
  let quarantined = 0;

  for (const raw of rawAll) {
    const res = migrateRecord(raw);
    if (res.status === 'ok') {
      scenarios.push(res.scenario);
      if (res.upgraded) await db.put('scenarios', asRecord(res.scenario));
    } else if (res.status === 'future') {
      scenarios.push(res.scenario);
      readOnlyIds.push(res.scenario.id);
    } else {
      quarantined++;
      const id = typeof (raw as { id?: unknown })?.id === 'string' ? (raw as { id: string }).id : crypto.randomUUID();
      await db.put('quarantine', { id, raw, error: res.error, quarantinedAt: new Date().toISOString() });
      await db.delete('scenarios', id);
    }
  }

  scenarios.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return { scenarios, readOnlyIds, quarantined };
}

/** Get and validate one scenario by id, or null if missing/invalid. */
export async function getScenario(id: string): Promise<Scenario | null> {
  const db = await getDb();
  const raw = await db.get('scenarios', id);
  if (!raw) return null;
  const res = migrateRecord(raw);
  return res.status === 'quarantine' ? null : res.scenario;
}

/** Upsert a scenario; stamps `updatedAt` and the current schema version (§9). */
export async function saveScenario(scenario: Scenario, setUpdatedAt = true): Promise<Scenario> {
  const db = await getDb();
  const toSave: Scenario = {
    ...scenario,
    updatedAt: setUpdatedAt ? new Date().toISOString() : scenario.updatedAt,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };
  await db.put('scenarios', asRecord(toSave));
  return toSave;
}

/** Create a new scenario from inputs (helper for "Save current as…"). */
export function newScenario(name: string, inputs: ScenarioInputs): Scenario {
  const now = new Date().toISOString();
  return { id: crypto.randomUUID(), name, createdAt: now, updatedAt: now, schemaVersion: CURRENT_SCHEMA_VERSION, inputs };
}

export async function deleteScenario(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('scenarios', id);
}

/** Duplicate a scenario under a new id and "(copy)" name. */
export async function duplicateScenario(id: string): Promise<Scenario | null> {
  const original = await getScenario(id);
  if (!original) return null;
  return saveScenario(newScenario(`${original.name} (copy)`, original.inputs));
}

export async function renameScenario(id: string, name: string): Promise<Scenario | null> {
  const original = await getScenario(id);
  if (!original) return null;
  return saveScenario({ ...original, name });
}
